import isUndefined from 'lodash-es/isUndefined';
import { BehaviorSubject, fromEvent, merge, noop, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, share, skip, startWith, tap } from 'rxjs/operators';
import { shallowEqualObjects } from 'shallow-equal';
import { BrowsingContextError } from '../../browsingContextError';
import { EDITOR_SELECTED_ELEMENT } from '../../helpers/dom/content';
import {
  createPointAtEndOfNodeContents,
  createPointAtStartOfNodeContents,
  createPointWithOffsetInContainer,
  findFirstTextPoint,
  findLastTextPoint,
  Point,
} from '../../helpers/dom/point';
import { normalizeSelection } from '../../helpers/elementHtmlEditor/selection';
import {
  createPointsWithSelection,
  createSelectionWithPoint,
  createSelectionWithPoints,
  createSelectionWithRange,
  isSelectionBackward,
  isSelectionContainedByNode,
  isSelectionPresent,
  PointSelection,
  SelectionLike,
  updateSelection,
} from '../../helpers/selection';
import { isDefined } from '../../helpers/types';
import { createConnectingService } from '../base/connectingService';
import { HtmlService } from '../html/htmlService';
import { ElementService } from './elementService';
import { FocusService } from './focusService';
import { NULL_SELECTION_SERVICE, SelectionService } from './selectionService';

export interface ElementSelection extends SelectionLike {
  readonly isCollapsed: boolean;
  readonly anchorNode: Node;
  readonly anchorOffset: number;
  readonly focusNode: Node;
  readonly focusOffset: number;

  readonly isBackward: boolean;
  readonly range: Range;
}

export interface ElementSelectionService extends SelectionService {
  readonly container?: Node;
  readonly selection?: ElementSelection;
  readonly extraSelectedElements: Iterable<Element>;

  withMutations<T>(callback: () => T): T;
  normalize(): void;

  moveToPoint(point: Point, shouldScrollIntoView?: boolean): void;
  moveToOffsetInContainer(node: Node, offset: number, shouldScrollIntoView?: boolean): void;

  selectWithPoints(points: PointSelection): void;
  selectWithRange(range: Range, isBackward: boolean): void;
  selectWithOffsetsInContainers(selection: SelectionLike): void;
}

export const NULL_ELEMENT_SELECTION_SERVICE: ElementSelectionService = {
  ...NULL_SELECTION_SERVICE,

  extraSelectedElements: [],

  withMutations: <T>(callback: () => T) => callback(),
  normalize: noop,
  moveToPoint: noop,
  moveToOffsetInContainer: noop,
  selectWithPoints: noop,
  selectWithRange: noop,
  selectWithOffsetsInContainers: noop,
};

export function createElementSelectionService(
  element$: Observable<HTMLElement | null>,
  focusService: FocusService,
  htmlService: HtmlService,
): ElementSelectionService {
  return createConnectingService(
    element$,
    () => NULL_ELEMENT_SELECTION_SERVICE,
    element => new BaseElementSelectionService(element, focusService, htmlService),
  );
}

class BaseElementSelectionService extends ElementService<HTMLElement> implements ElementSelectionService {
  constructor(element: HTMLElement, focusService: FocusService, htmlService: HtmlService) {
    const document = element.ownerDocument;
    if (!document) {
      throw new BrowsingContextError();
    }

    const selection$ = new BehaviorSubject<ElementSelection | undefined>(undefined);

    const memoryChanges$ = selection$.pipe(
      // De-dupe.
      distinctUntilChanged((x, y) => Boolean(x && y && shallowEqualObjects(x, y))),
      // Skip the initial emit from subscribing to the behavior subject.
      skip(1),
      // Never use selection from memory when element has focus.
      filter(() => !this.isActive),
    );

    const domChanges$ = merge(
      // When the DOM in the element is mutated. This also covers common cases, such as how pressing
      // backspace changes the selection without emitting a selectionchange event.
      htmlService.changes$,
      // When the selection changes anywhere in the document.
      fromEvent(document, 'selectionchange'),
    );

    super(element, focusService, {
      changes$: merge(memoryChanges$, domChanges$),
      effects$: merge(
        // Restore selection from memory when regaining focus.
        getDomRestores$(focusService).pipe(
          map(() => this.selectionInMemoryPrimitive),
          filter(isDefined),
          tap(selection => this.setSelectionInDom(selection)),
          share(),
        ),
        // Save selection to memory whenever it changes.
        domChanges$.pipe(
          map(() => this.selectionInDom),
          filter(isDefined),
          tap(selection => this.setSelectionInMemoryPrimitive(selection)),
          share(),
        ),
      ),
    });

    this.focusService = focusService;
    this.selection$ = selection$;
    this.isDomSelectionOutdated = false;
  }

  private readonly focusService: FocusService;
  private readonly selection$: BehaviorSubject<ElementSelection | undefined>;
  private isDomSelectionOutdated: boolean;

  get isActive() {
    return this.focusService.isActive;
  }

  get selectedText() {
    const { selection } = this;
    return selection && selection.range.toString();
  }

  get container(): Node | undefined {
    return this.element;
  }

  get selection(): ElementSelection | undefined {
    return this.selectionInDom || this.selectionInMemory || this.selectionAtEnd;
  }

  private get selectionInDom(): ElementSelection | undefined {
    // Continue to use the DOM selection even after an element loses focus, but stop using it after the memory selection
    // was changed.
    if (this.isDomSelectionOutdated) {
      return undefined;
    }

    // Do we have a valid selection in our element?
    const selection = this.window.getSelection();
    if (!isSelectionPresent(selection) || !isSelectionContainedByNode(this.element, selection)) {
      return undefined;
    }

    const { anchorNode, anchorOffset, focusNode, focusOffset } = selection;
    const range = selection.getRangeAt(0);
    return {
      isCollapsed: range.collapsed,
      anchorNode,
      anchorOffset,
      focusNode,
      focusOffset,
      isBackward: isSelectionBackward(selection),
      range,
    };
  }

  private get selectionInMemory(): ElementSelection | undefined {
    // Never use selection from memory when element has focus.
    return !this.isActive ? this.selectionInMemoryPrimitive : undefined;
  }

  private get selectionInMemoryPrimitive(): ElementSelection | undefined {
    return this.selection$.getValue();
  }

  private get selectionAtEnd(): ElementSelection | undefined {
    const { element } = this;
    const pointAtEnd = findLastTextPoint(element, element);
    if (!pointAtEnd) {
      return undefined;
    }

    const selection = createSelectionWithPoint(pointAtEnd);
    return selection && createElementSelection(selection);
  }

  private maybeSetSelection(selection: ElementSelection | SelectionLike | undefined) {
    if (!selection) {
      return;
    }

    this.setSelection(selection);
  }

  private setSelection(selection: ElementSelection | SelectionLike | undefined) {
    if (this.isActive) {
      this.setSelectionInDom(selection);
    } else {
      this.setSelectionInMemory(selection);
    }
  }

  private setSelectionInDom(newSelection: SelectionLike | undefined) {
    const selection = this.window.getSelection();
    if (!selection) {
      return;
    }

    if (!newSelection) {
      selection.removeAllRanges();
      this.isDomSelectionOutdated = false;
      return;
    }

    if (!isSelectionContainedByNode(this.element, newSelection)) {
      return;
    }

    updateSelection(selection, newSelection);
    this.isDomSelectionOutdated = false;
  }

  private setSelectionInMemory(selection: ElementSelection | SelectionLike | undefined) {
    const elementSelection =
      isUndefined(selection) || isElementSelection(selection) ? selection : createElementSelection(selection);

    this.setSelectionInMemoryPrimitive(elementSelection);
    this.isDomSelectionOutdated = this.isDomSelectionOutdated || Boolean(elementSelection);
  }

  private setSelectionInMemoryPrimitive(selection: ElementSelection | undefined) {
    this.selection$.next(selection);
  }

  withMutations<T>(callback: () => T): T {
    // Get a stable representation of our current selection, either from DOM or from memory.
    const selection = this.selectionInDom || this.selectionInMemory;
    const points = selection && createPointsWithSelection(selection);

    try {
      // Return the callback result. (This still executes our finally block!)
      return callback();
    } finally {
      // Update our selection with the stable representation.
      this.maybeSetSelection(points && createSelectionWithPoints(points));
    }
  }

  normalize() {
    const { selection } = this;
    if (!selection) {
      return;
    }

    const newSelection = normalizeSelection(this.element, selection);
    if (newSelection === selection) {
      return;
    }

    this.maybeSetSelection(newSelection);
  }

  moveToEnd() {
    this.maybeSetSelection(this.selectionAtEnd);
  }

  moveToPoint(point: Point) {
    const newSelection = createSelectionWithPoint(point);
    this.maybeSetSelection(newSelection);
  }

  moveToOffsetInContainer(node: Node, offset: number): void {
    this.maybeSetSelection({
      anchorNode: node,
      anchorOffset: offset,
      focusNode: node,
      focusOffset: offset,
    });
  }

  selectWithPoints(points: PointSelection) {
    const newSelection = createSelectionWithPoints(points);
    this.maybeSetSelection(newSelection);
  }

  selectWithRange(range: Range, isBackward: boolean) {
    const newSelection = createSelectionWithRange(range, isBackward);
    this.maybeSetSelection(newSelection);
  }

  selectWithOffsetsInContainers(newSelection: SelectionLike): void {
    this.maybeSetSelection(newSelection);
  }

  selectAll() {
    const { element } = this;
    const anchorPoint = findFirstTextPoint(element) || createPointAtStartOfNodeContents(element);
    const focusPoint = findLastTextPoint(element) || createPointAtEndOfNodeContents(element);
    const newSelection = createSelectionWithPoints({ anchorPoint, focusPoint });
    this.maybeSetSelection(newSelection);
  }

  deselect() {
    this.setSelection(undefined);
  }

  get extraSelectedElements() {
    return this.element.querySelectorAll(`[${EDITOR_SELECTED_ELEMENT}]`);
  }
}

function getDomRestores$(focusService: FocusService): Observable<unknown> {
  return focusService.changes$.pipe(
    startWith(undefined),
    map(() => {
      const { isActive, isPendingFocus } = focusService;
      return { isActive, isPendingFocus };
    }),
    pairwise(),
    filter(([prevState, nextState]) => {
      const { isActive: wasActive } = prevState;
      const { isActive, isPendingFocus } = nextState;

      // Try to restore the selection before the element is pro-actively focused, otherwise the browser's
      // default selection may cause inadvertent scroll.
      const willFocus = !isActive && isPendingFocus;
      const didFocus = !wasActive && isActive;
      return willFocus || didFocus;
    }),
  );
}

function isElementSelection(
  selection: ElementSelection | SelectionLike | undefined,
): selection is ElementSelection {
  return Boolean(
    selection && 'isCollapsed' in selection && 'isBackward' in selection && 'range' in selection,
  );
}

function createElementSelection({
  anchorNode,
  anchorOffset,
  focusNode,
  focusOffset,
}: SelectionLike): ElementSelection | undefined {
  const document = anchorNode.ownerDocument;
  if (!document) {
    return undefined;
  }

  const isCollapsed = anchorNode === focusNode && anchorOffset === focusOffset;

  const anchorPoint = createPointWithOffsetInContainer(anchorNode, anchorOffset);
  const focusPoint = createPointWithOffsetInContainer(focusNode, focusOffset);
  const isBackward = anchorPoint.isAfterPoint(focusPoint);

  const range = document.createRange();
  if (isBackward) {
    range.setStart(focusNode, focusOffset);
    range.setEnd(anchorNode, anchorOffset);
  } else {
    range.setStart(anchorNode, anchorOffset);
    range.setEnd(focusNode, focusOffset);
  }

  return {
    isCollapsed,
    anchorNode,
    anchorOffset,
    focusNode,
    focusOffset,
    isBackward,
    range,
  };
}
